import React, { useEffect, useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { formatCurrencyCOP, formatDate } from "../utils/format";
import { Users, Truck, User, Tag, Package, Box, Eye } from "lucide-react";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  User as UserIcon,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

function Dashboard() {
  const [data, setData] = useState({
    counts: { users: 0, suppliers: 0, clients: 0, categories: 0, brands: 0, presentations: 0, products: 0 },
    lastEntry: null,
    lastExit: null
  });

  const [chartData, setChartData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [clientExitFilters, setClientExitFilters] = useState({
    startDate: "",
    endDate: "",
  });
  const [appliedClientExitFilters, setAppliedClientExitFilters] = useState(null);
  const [clientExitReport, setClientExitReport] = useState({
    months: [],
    clients: [],
    grandTotal: 0,
  });
  const [clientExitLoading, setClientExitLoading] = useState(false);
  const [clientExitError, setClientExitError] = useState("");
  const [clientExitFilterError, setClientExitFilterError] = useState("");
  const [monthsPerPage, setMonthsPerPage] = useState(4);
  const [monthPage, setMonthPage] = useState(0);
  const navigate = useNavigate();

  const COLORS = ["#4CAF50", "#FF9800", "#2196F3", "#9C27B0", "#FF5722", "#607D8B", "#FFC107"];

  useEffect(() => {
    const ts = Date.now();
    api.get(`/dashboard?ts=${ts}`)
      .then(res => setData(res.data))
      .catch(() => console.error("Error al cargar datos"));

    api.get(`/dashboard/stats?ts=${ts}`)
      .then(res => setChartData(res.data))
      .catch(() => alert("Error al cargar estadísticas"));

    api.get(`/dashboard/categories-value?ts=${ts}`)
      .then(res => setCategoryData(res.data))
      .catch(() => alert("Error al cargar valores por categoría"));
  }, []);

  useEffect(() => {
    if (!appliedClientExitFilters) return undefined;

    let isCurrentRequest = true;

    const fetchClientExitReport = async () => {
      setClientExitLoading(true);
      setClientExitError("");

      try {
        const res = await api.get("/dashboard/client-monthly-exits", {
          params: appliedClientExitFilters,
        });
        if (isCurrentRequest) setClientExitReport(res.data);
      } catch (error) {
        console.error("Error al cargar salidas por área:", error);
        if (isCurrentRequest) {
          setClientExitError("No se pudo cargar el reporte de entregas por área.");
        }
      } finally {
        if (isCurrentRequest) setClientExitLoading(false);
      }
    };

    fetchClientExitReport();
    return () => {
      isCurrentRequest = false;
    };
  }, [appliedClientExitFilters]);

  useEffect(() => {
    const updateMonthsPerPage = () => {
      if (window.innerWidth < 640) {
        setMonthsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setMonthsPerPage(2);
      } else {
        setMonthsPerPage(4);
      }
    };

    updateMonthsPerPage();
    window.addEventListener("resize", updateMonthsPerPage);
    return () => window.removeEventListener("resize", updateMonthsPerPage);
  }, []);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(clientExitReport.months.length / monthsPerPage) - 1,
    );
    setMonthPage((page) => Math.min(page, lastPage));
  }, [clientExitReport.months.length, monthsPerPage]);

  const applyClientExitFilters = (event) => {
    event.preventDefault();
    setClientExitFilterError("");

    if (
      !clientExitFilters.startDate ||
      !clientExitFilters.endDate ||
      clientExitFilters.startDate > clientExitFilters.endDate
    ) {
      setClientExitFilterError("Selecciona un rango de fechas válido.");
      return;
    }

    setAppliedClientExitFilters({ ...clientExitFilters });
    setMonthPage(0);
  };

  const formatCurrency = (val) => `$${parseFloat(val).toLocaleString("es-CO")}`;
  const formatDateLocal = (dateStr) => new Date(dateStr).toLocaleDateString();

  // Formato abreviado para el eje Y
  const formatYAxis = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value}`;
  };

  // Formato COP para Tooltip
  const formatCurrencyCOP = (value) => {
    return `$${parseFloat(value).toLocaleString("es-CO")}`;
  };

  const totalInventario = categoryData.reduce((sum, item) => sum + item.value, 0);
  const clientsWithDeliveries = clientExitReport.clients.filter(
    (client) => Number(client.total) > 0,
  );
  const totalMonthPages = Math.ceil(clientExitReport.months.length / monthsPerPage);
  const visibleMonths = clientExitReport.months.slice(
    monthPage * monthsPerPage,
    (monthPage + 1) * monthsPerPage,
  );
  const firstVisibleMonth = monthPage * monthsPerPage + 1;
  const lastVisibleMonth = Math.min(
    (monthPage + 1) * monthsPerPage,
    clientExitReport.months.length,
  );

  const cards = [
    { title: "Usuarios", count: data.users, icon: <Users size={40} className="text-blue-500" />, link: "/users" },
    { title: "Proveedores", count: data.suppliers, icon: <Truck size={40} className="text-green-500" />, link: "/suppliers" },
    { title: "Clientes", count: data.clients, icon: <User size={40} className="text-purple-500" />, link: "/clients" },
    { title: "Categorías", count: data.categories, icon: <Tag size={40} className="text-yellow-500" />, link: "/categories" },
    { title: "Marcas", count: data.brands, icon: <Tag size={40} className="text-pink-500" />, link: "/brands" },
    { title: "Presentaciones", count: data.presentations, icon: <Box size={40} className="text-indigo-500" />, link: "/presentations" },
    { title: "Productos", count: data.products, icon: <Package size={40} className="text-red-500" />, link: "/products" },
    { title: "Kardex", count: data.products, icon: <Package size={40} className="text-red-500" />, link: "/kardex" }

  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Bienvenido al Dashboard</h2>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => navigate(card.link)}
            className="bg-white shadow-md rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer border border-gray-100
                      transition transform hover:shadow-xl hover:scale-105 duration-300"
          >
            <div className="mb-3">{card.icon}</div>
            <h3 className="text-lg font-semibold">{card.title}</h3>
            <p className="text-2xl font-bold">{card.count}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Card Última Entrada */}
        <div className="bg-white border-l-8 border-blue-500 shadow-md rounded-2xl p-6 flex items-center justify-between transition-all hover:shadow-lg">
          <div className="flex items-center gap-5">
            <div className="bg-blue-100 p-3.5 rounded-xl text-blue-600 shadow-sm">
              <ArrowDownLeft size={30} strokeWidth={2.5} />
            </div>

            <div className="flex flex-col justify-center">
              <p className="text-[12px] text-blue-500 font-bold uppercase tracking-[0.2em] mb-1">Ultima Entrada</p>
              <h3 className="text-xl font-extrabold text-gray-800 leading-tight mb-2">
                {data.lastEntry ? data.lastEntry.supplier?.name : "Sin registros"}
              </h3>

              <div className="flex items-center gap-3">
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg text-sm text-gray-600 font-semibold border border-gray-100">
                  <Calendar size={14} className="text-gray-400" />
                  {/* CAMBIO AQUÍ: Usando formatDate de utils */}
                  {data.lastEntry ? formatDate(data.lastEntry.date) : "--"}
                </span>
                <span className="bg-blue-50 px-3 py-1.5 rounded-lg text-base font-bold text-blue-700">
                  {/* CAMBIO AQUÍ: Usando formatCurrencyCOP de utils */}
                  {data.lastEntry ? formatCurrencyCOP(data.lastEntry.total_purchase) : "$0"}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => data.lastEntry && navigate(`/entries/${data.lastEntry.id}`)}
            className="bg-gray-50 hover:bg-blue-600 hover:text-white text-blue-500 p-3.5 rounded-xl transition-all duration-300 border border-gray-100 shadow-sm"
          >
            <Eye size={22} />
          </button>
        </div>

        {/* Card Última Salida */}
        <div className="bg-white border-l-8 border-red-500 shadow-md rounded-2xl p-6 flex items-center justify-between transition-all hover:shadow-lg">
          <div className="flex items-center gap-5">
            <div className="bg-red-100 p-3.5 rounded-xl text-red-600 shadow-sm">
              <ArrowUpRight size={30} strokeWidth={2.5} />
            </div>

            <div className="flex flex-col justify-center">
              <p className="text-[12px] text-red-500 font-bold uppercase tracking-[0.2em] mb-1">Ultima Salida</p>
              <h3 className="text-xl font-extrabold text-gray-800 leading-tight mb-2">
                {data.lastExit ? data.lastExit.client?.name : "Sin registros"}
              </h3>

              <div className="flex items-center gap-3">
                <span className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg text-sm text-gray-600 font-semibold border border-gray-100">
                  <Calendar size={14} className="text-gray-400" />
                  {/* CAMBIO AQUÍ: Usando formatDate de utils */}
                  {data.lastExit ? formatDate(data.lastExit.date) : "--"}
                </span>
                <span className="bg-red-50 px-3 py-1.5 rounded-lg text-base font-bold text-red-700">
                  {/* CAMBIO AQUÍ: Usando formatCurrencyCOP de utils */}
                  {data.lastExit ? formatCurrencyCOP(data.lastExit.total_exit) : "$0"}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={() => data.lastExit && navigate(`/exits/${data.lastExit.id}`)}
            className="bg-gray-50 hover:bg-red-600 hover:text-white text-red-500 p-3.5 rounded-xl transition-all duration-300 border border-gray-100 shadow-sm"
          >
            <Eye size={22} />
          </button>
        </div>
      </div>


      {/* Sección de Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gráfico de Barras */}
        <div className="bg-white shadow-md rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Movimientos de los últimos 6 meses</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis tickFormatter={formatYAxis} width={80} />
              <Tooltip
                formatter={(value) => formatCurrencyCOP(value)}
                labelFormatter={(label) => `Mes: ${label}`}
              />
              <Legend />
              <Bar dataKey="entradas" fill="#b0e3ff" name="Entradas" isAnimationActive />
              <Bar dataKey="salidas" fill="#fea199" name="Salidas" isAnimationActive />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Dona */}
        <div className="bg-white shadow-md rounded-xl p-6 flex flex-col items-center">
          <h3 className="text-lg font-semibold mb-2">Valor de Inventario por Categoría</h3>

          {/* Total del inventario */}
          <div className="text-xl font-bold mb-4 text-gray-700">
            Total: {formatCurrencyCOP(totalInventario)}
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={categoryData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={130}
                fill="#8884d8"
                paddingAngle={2}
                label={false}
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [formatCurrencyCOP(value), name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <section className="bg-white shadow-md rounded-xl p-6 mt-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold">Entregas por área</h3>
            <p className="text-sm text-gray-500 mt-1">
              Valor de los productos entregados por mes, incluidas las salidas directas y las entregas de solicitudes.
            </p>
          </div>

          <div className="bg-red-50 rounded-lg px-4 py-3">
            <p className="text-xs font-semibold uppercase text-red-600">Total del período</p>
            <p className="text-xl font-bold text-red-800">
              {!appliedClientExitFilters
                ? "—"
                : clientExitLoading
                ? "Cargando..."
                : clientExitError
                  ? "—"
                  : formatCurrencyCOP(clientExitReport.grandTotal)}
            </p>
          </div>
        </div>

        <form
          onSubmit={applyClientExitFilters}
          className="bg-gray-50 rounded-lg p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-4 items-end"
        >
          <div>
            <label htmlFor="client-exits-start-date" className="block text-sm font-semibold mb-1">
              Desde
            </label>
            <input
              id="client-exits-start-date"
              type="date"
              value={clientExitFilters.startDate}
              onChange={(event) =>
                setClientExitFilters((current) => ({
                  ...current,
                  startDate: event.target.value,
                }))
              }
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <div>
            <label htmlFor="client-exits-end-date" className="block text-sm font-semibold mb-1">
              Hasta
            </label>
            <input
              id="client-exits-end-date"
              type="date"
              value={clientExitFilters.endDate}
              onChange={(event) =>
                setClientExitFilters((current) => ({
                  ...current,
                  endDate: event.target.value,
                }))
              }
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded h-10"
          >
            Aplicar fechas
          </button>
          {clientExitFilterError && (
            <p className="text-sm text-red-600 sm:col-span-2 lg:col-span-3" role="alert">
              {clientExitFilterError}
            </p>
          )}
        </form>

        {!appliedClientExitFilters ? (
          <p className="text-center text-gray-500 py-8">
            Selecciona las fechas de inicio y fin para consultar las entregas por área.
          </p>
        ) : clientExitLoading ? (
          <p className="text-center text-gray-500 py-8">Cargando entregas por área...</p>
        ) : clientExitError ? (
          <p className="text-center text-red-600 py-8" role="alert">{clientExitError}</p>
        ) : (
          <>
            {totalMonthPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <p className="text-sm text-gray-600" aria-live="polite">
                  Meses {firstVisibleMonth}–{lastVisibleMonth} de {clientExitReport.months.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMonthPage((page) => Math.max(0, page - 1))}
                    disabled={monthPage === 0}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    aria-label="Mostrar meses anteriores"
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {monthPage + 1} de {totalMonthPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setMonthPage((page) => Math.min(totalMonthPages - 1, page + 1))
                    }
                    disabled={monthPage >= totalMonthPages - 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    aria-label="Mostrar meses siguientes"
                  >
                    Siguiente
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            <table className="w-full text-xs sm:text-sm text-right">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-700">
                  <th className="px-2 sm:px-4 py-3 text-left">Área</th>
                  {visibleMonths.map((month) => (
                    <th key={month.key} className="px-2 sm:px-4 py-3">
                      {month.label}
                    </th>
                  ))}
                  <th className="px-2 sm:px-4 py-3">Total del período</th>
                </tr>
              </thead>
              <tbody>
                {clientsWithDeliveries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleMonths.length + 2}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No hay áreas con entregas en el período seleccionado.
                    </td>
                  </tr>
                ) : (
                  clientsWithDeliveries.map((client) => (
                    <tr key={client.clientId} className="border-b last:border-0 hover:bg-gray-50">
                      <th className="px-2 sm:px-4 py-3 text-left font-medium text-gray-800 break-words">
                        {client.name}
                      </th>
                      {visibleMonths.map((month) => (
                        <td key={month.key} className="px-2 sm:px-4 py-3 break-words">
                          {formatCurrencyCOP(client.monthlyTotals[month.key] || 0)}
                        </td>
                      ))}
                      <td className="px-2 sm:px-4 py-3 font-semibold break-words">
                        {formatCurrencyCOP(client.total)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-gray-50 font-bold text-gray-800">
                  <th className="px-2 sm:px-4 py-3 text-left">Total general</th>
                  {visibleMonths.map((month) => {
                    const monthTotal = clientExitReport.clients.reduce(
                      (sum, client) => sum + Number(client.monthlyTotals[month.key] || 0),
                      0,
                    );
                    return (
                      <td key={month.key} className="px-2 sm:px-4 py-3 break-words">
                        {formatCurrencyCOP(monthTotal)}
                      </td>
                    );
                  })}
                  <td className="px-2 sm:px-4 py-3 break-words">
                    {formatCurrencyCOP(clientExitReport.grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
